import React, { useEffect, useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import {
  LayoutDashboard, Inbox, Users, Building2, Hammer, Settings as SettingsIcon, LogOut,
  Calendar, HardHat, Truck, Sunrise, Activity, TrendingDown, ShieldCheck, Brain, HeartPulse,
  ClipboardList, FileText, GitBranch, TrendingUp, Phone, Wallet, Target, Home, ChevronDown,
} from "lucide-react";
import { useAuth } from "../lib/auth";
import api from "../lib/api";
import { subscribeOps } from "../lib/ops-bus";
import HelpSystem from "./HelpSystem";

// === PRIMARY NAV — the only thing a new staff member needs to learn (Phase B). ===
const NAV_PRIMARY = [
  { to: "/",          icon: Home,      label: "Home",      end: true },
  { to: "/pipeline",  icon: GitBranch, label: "Jobs" },
  { to: "/schedule",  icon: Calendar,  label: "Schedule" },
  { to: "/customers", icon: Users,     label: "Customers" },
  { to: "/projects",  icon: Building2, label: "Job list" },
];

// === MORE TOOLS — specialist screens. Collapsed by default. ===
// Grouped only to reduce visual density once expanded; not surfaced as primary.
const NAV_MORE_GROUPS = [
  {
    label: "Money & follow-up",
    items: [
      { to: "/chase",       icon: Target,     label: "Chasing list" },
      { to: "/revenue",     icon: TrendingUp, label: "Money tracker" },
      { to: "/follow-ups",  icon: Phone,      label: "Follow-ups" },
      { to: "/deposits",    icon: Wallet,     label: "Deposits" },
    ],
  },
  {
    label: "Daily work",
    items: [
      { to: "/leads",     icon: Inbox,         label: "Enquiries" },
      { to: "/surveys",   icon: ClipboardList, label: "Site visits" },
      { to: "/quotes",    icon: FileText,      label: "Quotes" },
      { to: "/dashboard", icon: LayoutDashboard, label: "Old home" },
    ],
  },
  {
    label: "Power tools",
    items: [
      { to: "/today",        icon: Sunrise,       label: "Today" },
      { to: "/signals",      icon: Activity,      label: "Trouble spots" },
      { to: "/delays",       icon: TrendingDown,  label: "Slipping jobs" },
      { to: "/intelligence", icon: Brain,         label: "Patterns" },
      { to: "/ops-health",   icon: HeartPulse,    label: "System health" },
      { to: "/audit",        icon: ShieldCheck,   label: "Activity log" },
    ],
  },
  {
    label: "Office records",
    items: [
      { to: "/crews",     icon: HardHat, label: "Crews" },
      { to: "/resources", icon: Truck,   label: "Equipment" },
      { to: "/pricelist", icon: Hammer,  label: "Price List" },
    ],
  },
];

// Role-based nav allowlists. Each role only sees the paths that map to its
// daily workflow. Admin (and unknown roles, e.g. legacy 'super_admin') see all.
// Pass HARDEN-6: removes the 19-item firehose for non-admin operators.
const ROLE_NAV = {
  admin: null,            // null = no filter (sees everything)
  estimator: new Set([
    "/", "/dashboard", "/pipeline", "/surveys", "/quotes", "/follow-ups",
    "/projects", "/pricelist", "/deposits", "/chase",
  ]),
  sales: new Set([
    "/", "/dashboard", "/pipeline", "/leads", "/quotes", "/revenue", "/follow-ups",
    "/projects", "/customers", "/deposits", "/chase",
  ]),
  viewer: new Set([
    "/", "/dashboard", "/pipeline", "/projects", "/customers",
  ]),
};

function filterNav(items, role) {
  const allow = ROLE_NAV[role];
  if (allow === undefined) return items;  // unknown role → show everything (safe default for admin-likes)
  if (allow === null) return items;        // explicit "all"
  return items.filter((i) => allow.has(i.to));
}

const STATUS_MAP = {
  block: { color: "#D63B3B", label: "Blockers", glow: "0 0 8px rgba(214,59,59,0.85)" },
  warn:  { color: "#D89034", label: "Warnings", glow: "0 0 6px rgba(216,144,52,0.7)" },
  ok:    { color: "#2C7C4B", label: "All clear", glow: "0 0 6px rgba(44,124,75,0.6)" },
  unknown: { color: "#6C6E86", label: "—", glow: "none" },
};

function useLiveStatus() {
  const [status, setStatus] = useState("unknown");
  const [counts, setCounts] = useState({ blocks: 0, warns: 0, jobs: 0 });
  useEffect(() => {
    let cancelled = false;
    let timer;
    const tick = async () => {
      try {
        const { data } = await api.get("/api/ops/today");
        if (cancelled) return;
        const conflicts = data.conflicts || [];
        const b = conflicts.filter((c) => c.severity === "block").length;
        const w = conflicts.filter((c) => c.severity === "warn").length;
        setCounts({ blocks: b, warns: w, jobs: (data.blocks || []).length });
        setStatus(b > 0 ? "block" : w > 0 ? "warn" : "ok");
      } catch (_) {
        if (!cancelled) setStatus("unknown");
      } finally {
        if (!cancelled) timer = setTimeout(tick, 60_000);
      }
    };
    tick();
    // Subscribe to ops-bus so the indicator refreshes immediately after mutations
    const unsub = subscribeOps(() => {
      clearTimeout(timer);
      tick();
    });
    return () => { cancelled = true; clearTimeout(timer); unsub(); };
  }, []);
  return { status, counts };
}

export default function AppShell() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const { status, counts } = useLiveStatus();
  const s = STATUS_MAP[status] || STATUS_MAP.unknown;
  const statusLabel = status === "block" ? `${counts.blocks} blocker${counts.blocks === 1 ? "" : "s"}` :
                       status === "warn"  ? `${counts.warns} warning${counts.warns === 1 ? "" : "s"}` :
                       status === "ok"    ? "All clear" : "—";
  const initials = (user?.name || user?.email || "?")
    .split(/[\s@.]+/).filter(Boolean).slice(0, 2).map((s) => s[0].toUpperCase()).join("");

  return (
    <div className="grid h-screen" style={{ gridTemplateColumns: "264px 1fr" }} data-testid="app-shell">
      {/* Sidebar — dark / commercial */}
      <aside
        className="flex flex-col text-white"
        style={{
          background: "var(--bg-nav)",
          borderRight: "1px solid rgba(255,255,255,0.06)",
        }}
      >
        {/* Brand block — integrated masthead on dark, no boxed treatment */}
        <div className="px-4 pt-5 pb-4" style={{ borderBottom: "1px solid rgba(255,255,255,0.08)" }}>
          <div className="flex items-center gap-2.5">
            <img
              src="/brand/mr-mark-512.png"
              alt=""
              aria-hidden="true"
              style={{ width: 28, height: 28, objectFit: "contain", display: "block", flexShrink: 0 }}
              data-testid="brand-mark"
            />
            <div className="min-w-0 leading-tight">
              <div
                className="text-[13px] font-extrabold uppercase tracking-[0.06em] text-white truncate"
                style={{ letterSpacing: "0.04em" }}
              >
                Manchester Roofing
              </div>
              <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-white/40 mt-[1px]">
                Australia · OS
              </div>
            </div>
          </div>
          <div className="mt-3 flex items-center gap-2" data-testid="live-status-strip"
               title={`${counts.jobs} job(s) scheduled today · ${statusLabel}`}>
            <span className="w-1.5 h-1.5 rounded-full"
                  data-testid={`live-status-${status}`}
                  style={{ background: s.color, boxShadow: s.glow }} />
            <span className="text-[9.5px] font-bold uppercase tracking-[0.22em] text-white/55">
              Operations
            </span>
            <span className="ml-auto text-[9px] font-extrabold uppercase tracking-[0.16em]"
                  style={{ color: s.color }}>
              {statusLabel}
            </span>
          </div>
        </div>

        {/* Nav */}
        <nav className="flex-1 px-3 pt-4 overflow-y-auto" data-testid={`nav-role-${user?.role || "unknown"}`}>
          <NavGroup label="Workflow" items={filterNav(NAV_PRIMARY, user?.role)} />
          <MoreToolsDrawer role={user?.role} />
        </nav>

        {/* Account strip */}
        <div className="px-3 pb-4 pt-3" style={{ borderTop: "1px solid rgba(255,255,255,0.08)" }}>
          <NavLink
            to="/settings"
            data-testid="nav-settings"
            className={({ isActive }) =>
              `flex items-center gap-3 px-3 py-2 rounded-md text-[13px] font-semibold mb-2 ${
                isActive ? "text-white" : "text-white/65 hover:bg-white/[0.06] hover:text-white"
              }`
            }
            style={({ isActive }) => isActive ? { background: "var(--brand)" } : undefined}
          >
            <SettingsIcon size={16} className="opacity-80" /> Settings
          </NavLink>

          <div className="flex items-center gap-3 px-2 py-2 rounded-md" style={{ background: "rgba(255,255,255,0.04)" }}>
            <div
              className="w-9 h-9 rounded-md flex items-center justify-center text-[12px] font-bold"
              style={{ background: "var(--accent)", color: "#fff" }}
              data-testid="user-avatar"
            >
              {initials}
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-[12px] font-semibold truncate text-white">{user?.name || user?.email}</div>
              <div className="text-[10px] uppercase tracking-[0.12em] text-white/50 font-bold">{user?.role}</div>
            </div>
            <button
              data-testid="logout-btn"
              aria-label="Sign out"
              onClick={async () => { await logout(); nav("/login"); }}
              className="p-1.5 rounded-md text-white/60 hover:text-white hover:bg-white/[0.08]"
              title="Sign out"
            >
              <LogOut size={14} />
            </button>
          </div>
        </div>
      </aside>

      {/* Main */}
      <main className="overflow-y-auto" style={{ background: "var(--bg)" }}>
        <Outlet />
      </main>

      {/* Help — floating button + slide-up panel, available from every page. */}
      <HelpSystem />
    </div>
  );
}


function NavGroup({ label, items, muted = false }) {
  if (!items || items.length === 0) return null;
  return (
    <div className={muted ? "mt-5" : "mb-1"}>
      <div
        className={"text-[9.5px] uppercase tracking-[0.18em] px-3 mb-1.5 font-extrabold " +
                   (muted ? "text-white/30" : "text-white/45")}
      >
        {label}
      </div>
      {items.map((item) => (
        <NavLink
          key={item.to}
          to={item.to}
          end={item.end}
          data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
          className={({ isActive }) =>
            "group relative flex items-center gap-3 px-3 py-2 rounded-md text-[13px] font-semibold mb-0.5 transition-colors " +
            (isActive ? "text-white" : (muted ? "text-white/50 hover:bg-white/[0.05] hover:text-white/85" : "text-white/65 hover:bg-white/[0.06] hover:text-white"))
          }
          style={({ isActive }) => isActive ? { background: "var(--brand)" } : undefined}
        >
          {({ isActive }) => (
            <>
              {isActive && (
                <span className="absolute left-0 top-1.5 bottom-1.5 w-[3px] rounded-r" style={{ background: "var(--accent)" }} />
              )}
              <item.icon size={muted ? 15 : 17} className={isActive ? "" : "opacity-80"} />
              <span className="flex-1">{item.label}</span>
            </>
          )}
        </NavLink>
      ))}
    </div>
  );
}


const MORE_OPEN_KEY = "mr_nav_more_open";

function MoreToolsDrawer({ role }) {
  const [open, setOpen] = useState(() => {
    try { return localStorage.getItem(MORE_OPEN_KEY) === "1"; }
    catch { return false; }
  });
  const groups = NAV_MORE_GROUPS
    .map((g) => ({ ...g, items: filterNav(g.items, role) }))
    .filter((g) => g.items.length > 0);
  if (groups.length === 0) return null;

  const toggle = () => {
    const next = !open;
    setOpen(next);
    try { localStorage.setItem(MORE_OPEN_KEY, next ? "1" : "0"); } catch (_) { /* ignore */ }
  };

  const totalItems = groups.reduce((n, g) => n + g.items.length, 0);

  return (
    <div className="mt-6" data-testid="nav-more-drawer">
      <button
        type="button"
        onClick={toggle}
        data-testid="nav-more-toggle"
        aria-expanded={open}
        className="w-full flex items-center gap-2 px-3 py-2 rounded-md text-[11px] font-extrabold uppercase tracking-[0.18em] text-white/45 hover:bg-white/[0.05] hover:text-white/70 transition-colors"
      >
        <ChevronDown
          size={13}
          className={`opacity-70 transition-transform ${open ? "" : "-rotate-90"}`}
        />
        <span className="flex-1 text-left">More tools</span>
        <span className="text-[10px] font-mono opacity-60">{totalItems}</span>
      </button>
      {open && (
        <div className="mt-1" data-testid="nav-more-content">
          {groups.map((g) => (
            <NavGroup key={g.label} label={g.label} items={g.items} muted />
          ))}
        </div>
      )}
    </div>
  );
}
